import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface FilterValue {
  id: number;
  value: string;
  active: boolean;
  position: number;
  physiotherapists_count?: number;
}

interface FilterCategory {
  id: number;
  name: string;
  key: string;
  description: string | null;
  active: boolean;
  position: number;
  filter_values: FilterValue[];
}

interface Props {
  categories: FilterCategory[];
  selectedValues: number[];
  onSelectionChange: (valueIds: number[]) => void;
  title?: string;
}

export default function FilterSelector({ categories, selectedValues, onSelectionChange, title = "Filtres" }: Props) {
  const [openCategories, setOpenCategories] = React.useState<Set<number>>(new Set());

  const toggleCategory = (categoryId: number) => {
    const newOpen = new Set(openCategories);
    if (newOpen.has(categoryId)) {
      newOpen.delete(categoryId);
    } else {
      newOpen.add(categoryId);
    }
    setOpenCategories(newOpen);
  };

  const handleValueToggle = (valueId: number) => {
    const newSelection = new Set(selectedValues);
    if (newSelection.has(valueId)) {
      newSelection.delete(valueId);
    } else {
      newSelection.add(valueId);
    }
    onSelectionChange(Array.from(newSelection));
  };

  const getSelectedCountForCategory = (category: FilterCategory) => {
    return category.filter_values.filter(value => selectedValues.includes(value.id)).length;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="outline">
            {selectedValues.length} sélectionné(s)
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {categories.map((category) => (
          <Collapsible
            key={category.id}
            open={openCategories.has(category.id)}
            onOpenChange={() => toggleCategory(category.id)}
          >
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  {openCategories.has(category.id) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  <div>
                    <h3 className="font-medium">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-gray-500">{category.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">
                    {getSelectedCountForCategory(category)} / {category.filter_values.length}
                  </Badge>
                </div>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                {category.filter_values
                  .filter(value => value.active)
                  .sort((a, b) => a.position - b.position)
                  .map((value) => (
                    <div key={value.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`filter-${value.id}`}
                        checked={selectedValues.includes(value.id)}
                        onCheckedChange={() => handleValueToggle(value.id)}
                      />
                      <label
                        htmlFor={`filter-${value.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                      >
                        {value.value}
                      </label>
                      {value.physiotherapists_count !== undefined && (
                        <Badge variant="outline" className="text-xs">
                          {value.physiotherapists_count}
                        </Badge>
                      )}
                    </div>
                  ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        ))}
      </CardContent>
    </Card>
  );
} 
